<template>
  <artmisads-modal
    v-model:open="props.showPaymentModal"
    title="Add Payment Record"
    :width="1000"
    :onCancel="() => props.setShowPaymentModal(false)"
    :destroyOnClose="true"
  >
    <a-spin :spinning="spinning">
      <div class="payment-confirm">
        <div class="title">
          Publisher Info
        </div>
        <div class="item">
          <span>ArtmisAds Account:</span>
          <span>{{detailData.publisherName}} / {{detailData.publisherEmail}}</span>
        </div>
        <div class="content border">
          <div class="item">
            <span>Bank Account Name:</span>
            <span></span>
          </div>
          <div class="item">
            <span>Bank Routing Number:</span>
            <span></span>
          </div>
          <div class="item">
            <span>Bank Account Number:</span>
            <span></span>
          </div>
        </div>
        <div class="content top">
        <div class="title">
          Payment Info
        </div>
          <div class="item">
            <span>Payment Amount:</span>
            <span>{{AMAZON_CURRENCY_MAPPER[detailData.marketplace]}}{{detailData.amount}}</span>
          </div>
          <div class="item">
            <span>Seller Status:</span>
            <!-- <span>{{FINANCE_SELLER_PAYMENT_STATUS_MAP[detailData.sellerPayStatus]?.label}}</span> -->
            <span>{{detailData.sellerPayDisplayStatus}}</span>
          </div>
          <div class="item">
            <span>Payment Status:</span>
            <!-- <span>{{FINANCE_SELLER_STATUS_MAP[detailData.status]?.label}}</span> -->
            <span>{{detailData.displayStatus}}</span>
          </div>
        </div>
        <div class="content border">
          <div class="item">
            <span>Pending:</span>
            <span>{{AMAZON_CURRENCY_MAPPER[detailData.marketplace]}}{{detailData.pendingAmount}}</span>
          </div>
          <div class="item">
            <span>Ready To Pay:</span>
            <span>{{AMAZON_CURRENCY_MAPPER[detailData.marketplace]}}{{detailData.readyToPayAmount}}</span>
          </div>
          <div class="item">
            <span>Paid:</span>
            <span>{{AMAZON_CURRENCY_MAPPER[detailData.marketplace]}}{{detailData.paidAmount}}</span>
          </div>
        </div>
        <div class="brand-select">
          <div class="title">
            Add Payment Record
          </div>
          <p class="brand-select__hint">Select brands for this payment</p>
          <div class="brand-select__table">
            <artmisads-table
              :columns="brandColumns"
              row-key="brandId"
              :data-source="brandList"
              :pagination="{
                total: brandListTotal,
                current: brandParams.page,
                pageSize: brandParams.size,
                showSizeChanger: true,
              }"
              :scroll="{ y: 240 }"
              :row-selection="{
                selectedRowKeys,
                onChange: onBrandSelectChange,
                getCheckboxProps: (record) => ({
                  disabled: !record.checkable,
                }),
              }"
              :change="onBrandTableChange"
              tableLayout="fixed"
            >
              <template #bodyCell="{ column, record, text }">
                <template v-if="column.name === 'showCurrency'">
                  {{ getBrandCurrency(record) }} {{ formatAmount(text) }}
                </template>
                <template v-if="column.name === 'brandPaymentStatus'">
                  {{ record.brandPaymentDisplayStatus || '--' }}
                </template>
                <template v-if="column.name === 'artemisAdsPaymentStatus'">
                  {{ record.artemisAdsPaymentDisplayStatus || '--' }}
                </template>
              </template>
            </artmisads-table>
          </div>
          <div class="brand-select__summary">
            <span>Selected Brands: <strong>{{ selectedBrandCount }}</strong></span>
            <span>TotalCommissions: <strong>{{ AMAZON_CURRENCY_MAPPER[detailData.marketplace] }} {{ formatAmount(totalCommissions) }}</strong></span>
          </div>
        </div>
        <div class="bottom-cont">
          <div class="amount">
            <span>Record Amount</span>
            <a-input-number  v-model:value="amountValue"/>
          </div>
          <div class="amount">
            <span>Comment</span>
            <artmisads-input v-model:value="commentValue"/>
          </div>
        </div>
      </div>
    </a-spin>
    <template #footer>
      <div class="btn">
        <artmisads-button @click="() => props.setShowPaymentModal(false)">Cancel</artmisads-button>
        <artmisads-button type="primary" @click="financePublisherBillingsDetailsConfirmToPay">Confirm</artmisads-button>
      </div>
    </template>
  </artmisads-modal>
</template>

<script setup>
import { Modal } from 'ant-design-vue';
import { message } from "ant-design-vue";
import { AMAZON_CURRENCY_MAPPER, CURRENCY_DATA_MAPPER } from '~/contants/common';
import { ref, onBeforeMount, computed, watch } from "vue";
import { financeApi } from "~/server/operator/finance";
import { FINANCE_SELLER_STATUS_MAP, FINANCE_SELLER_PAYMENT_STATUS_MAP } from '~/contants/operator';

const props = defineProps(["showPaymentModal", "billingId", 'setShowPaymentModal']);

const amountValue = ref('');
const commentValue = ref('');
const detailData = ref({});
const spinning = ref(false);
const brandList = ref([]);
const selectedRowKeys = ref([]);
const selectedBrandCommissions = ref({});
const brandListTotal = ref(0);
const brandParams = ref({
  page: 1,
  size: 10,
});

const brandColumns = [
  {
    title: 'Brand',
    dataIndex: 'brandName',
    width: 140,
    sorter: true,
  },
  {
    title:'sales',
    dataIndex: 'salesAmount',
    width: 140,
    name: 'showCurrency',
    sorter: true,
  },
  {
    title: 'Commissions',
    dataIndex: 'commissionAmount',
    width: 140,
    name: 'showCurrency',
    sorter: true,
  },
  {
    title: 'BrandPaymentStatus',
    dataIndex: 'brandPaymentStatus',
    width: 180,
    name: 'brandPaymentStatus',
    sorter: true,
  },
  {
    title: 'ArtemisAdsPaymentStatus',
    dataIndex: 'artemisAdsPaymentStatus',
    width: 220,
    name: 'artemisAdsPaymentStatus',
    sorter: true,
  },
];

const getBrandCurrency = (record) =>
  CURRENCY_DATA_MAPPER[record.currency] ||
  AMAZON_CURRENCY_MAPPER[detailData.value.marketplace] ||
  '';

const selectedBrandCount = computed(() => selectedRowKeys.value.length);

const totalCommissions = computed(() => {
  return Object.values(selectedBrandCommissions.value)
    .reduce((sum, amount) => sum + Number(amount || 0), 0);
});

const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return '--';
  return Number(value).toFixed(2);
};

const onBrandSelectChange = (keys) => {
  const currentPageBrandIds = brandList.value.map(item => item.brandId);
  const preservedKeys = selectedRowKeys.value.filter(
    id => !currentPageBrandIds.includes(id)
  );
  selectedRowKeys.value = [...preservedKeys, ...keys];

  currentPageBrandIds.forEach((id) => {
    if (!keys.includes(id)) {
      delete selectedBrandCommissions.value[id];
    }
  });
  brandList.value.forEach((item) => {
    if (keys.includes(item.brandId)) {
      selectedBrandCommissions.value[item.brandId] = item.commissionAmount;
    }
  });
};

const onBrandTableChange = (pagination, _filters, sorter) => {
  const { current, pageSize } = pagination;
  brandParams.value.page = current;
  brandParams.value.size = pageSize;
  getBrandList(sorter);
};

// watch(totalCommissions, (value) => {
//   amountValue.value = value ? Number(value.toFixed(2)) : '';
// });

const toMoneyCents = (value) => Math.round(Number(value) * 100)
const getRemainingPayAmount = (amount, paidAmount) =>
  (toMoneyCents(amount) - toMoneyCents(paidAmount)) / 100

const getBrandList = async (sorter) => {
  const params = {
    billingId: props.billingId,
    page: brandParams.value.page,
    size: brandParams.value.size,
  };
  if (sorter?.field && sorter?.order) {
    params.sortField = sorter.field;
    params.sortDirection = sorter.order === 'ascend' ? 'asc' : 'desc';
  }
  const res = await financeApi.financePublisherBillingsDetailsPaymentBrands(params);
  if (res?.data) {
    brandListTotal.value = res.data.total || 0;
    brandList.value = res.data.data || [];
  }
};

const getFinancePublisherBillingsDetailsPendingEdit = async () => {
  spinning.value = true;
  const params = { billingId: props.billingId };
  const res = await financeApi.getFinancePublisherBillingsDetailsPendingEdit(
    params
  );
  if (res) {
    detailData.value = res;
  }
  await getBrandList();
  spinning.value = false;
};

const financePublisherBillingsDetailsConfirmToPay = async () => {
  // console.log(selectedRowKeys.value,'数据')
  // if (!selectedRowKeys.value.length) {
  //   return message.error('Please select at least one brand');
  // }
  if (!amountValue.value) {
    return message.error('Please write amount');
  }
  // const remainingAmount = getRemainingPayAmount(
  //   detailData.value.amount,
  //   detailData.value.paidAmount,
  // )
  // if (toMoneyCents(amountValue.value) > toMoneyCents(remainingAmount)) {
  //   return  Modal.error({
  //   title: 'Notice',
  //   content: `Adding this record will cause the amount of paid to exceed the amount of payment(${AMAZON_CURRENCY_MAPPER[detailData.value.marketplace]}${detailData.value.amount}).Plese check again.`,
  // });
  // }

  Modal.confirm({
    title: 'Notice',
    content: `Are you sure to ${AMAZON_CURRENCY_MAPPER[detailData.value.marketplace]}${amountValue.value} add to paid amount?`,
    onOk: async () => {
      spinning.value = true;
      const params = {
        billingId: props.billingId,
        amountToPay: amountValue.value,
        comment: commentValue.value,
        brandIdList: selectedRowKeys.value,
      }
      const res = await financeApi.financePublisherBillingsDetailsConfirmToPay(params);
      if (res === null) {
        message.success('Success');
        props.setShowPaymentModal(false, true);
      }
      spinning.value = false;
      }
    });
  };

onBeforeMount(() => {
  getFinancePublisherBillingsDetailsPendingEdit();
});
</script>

<style lang="less" scoped>
.payment-confirm {
  .title {
    flex-basis: 100%;
    margin-bottom: 12px;
    padding-left: 12px;
    border-left: 3px solid #7653DA;
    color: var(--dark-1);
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
    letter-spacing: 0.4px;
  }
  .item {
    margin-bottom: 10px;
    > span:nth-child(2) {
      color: var(--dark-1);
      font-weight: 600;
      font-size: 14px;
      letter-spacing: 0.3px;
      margin-left:8px;
    }
    img {
      width: 100px;
      height: 100px;
      object-fit: contain;
      border-radius: 12px;
      margin-left:8px;
    }
  }
  .content {
    display: flex;
    flex-wrap: wrap;
    &.top {
      margin-top: 20px;
    }
    &.border {
      padding-bottom: 20px;
      border-bottom: 1px solid var(--border-4);
    }
    .item {
      width: 230px;
    }
  }
  .brand-select {
    margin-top: 20px;

    &__hint {
      margin-bottom: 12px;
      color: var(--dark-2);
      font-size: 14px;
      line-height: 20px;
    }

    // &__table {
    //   border: 1px solid var(--border-4);
    //   border-radius: 12px;
    //   overflow: hidden;

    //   :deep(.ant-table) {
    //     .ant-table-thead > tr > th {
    //       background: #f5f5f7;
    //       border-bottom: none;
    //     }

    //     .ant-table-tbody > tr > td {
    //       border-bottom: none;
    //     }
    //   }
    // }

    &__summary {
      display: flex;
      gap: 32px;
      margin-top: 12px;
      color: var(--dark-2);
      font-size: 14px;
      line-height: 20px;

      strong {
        color: var(--dark-1);
        font-weight: 600;
      }
    }
  }
  .bottom-cont {
    margin-top: 20px;
    .amount {
      display: flex;
      width: 500px;
      margin-bottom: 20px;
      > span {
        align-self: center;
        width: 120px;
        margin-right: 8px;
      }
    }
    .receipt {
      margin-top: 20px;
    }
  }
}
</style>
